import { useTranslation } from 'react-i18next';
import { Chip, Popover } from '@c2fo/liquidity';
import { ExclamationTriangleIcon } from '@c2fo/liquidity/icons';
import useLocaleFormat from '@/utils/useLocaleFormat';

interface ClosedMarketChipProps {
  /**
   * When provided, renders a count-based label ("N Closed Market(s)") and a
   * dynamic tooltip that includes the invoice amount. When absent, falls back
   * to the generic "Closed Market" label and generic tooltip.
   */
  count?: number;
  /**
   * Pre-formatted invoice amount string (e.g. "$1,234.56") to include in the
   * dynamic tooltip. Only used when `count` is also provided.
   */
  invoiceAmount?: string;
}

/**
 * Chip indicating that one or more markets are currently closed, with a hover
 * tooltip explaining that those invoices are unavailable for early payment.
 */
export default function ClosedMarketChip({ count, invoiceAmount }: ClosedMarketChipProps) {
  const { t } = useTranslation();
  const { asNumber } = useLocaleFormat();

  const label =
    count !== undefined
      ? t('core.closedMarketWithCount', { count, formattedCount: asNumber({ value: count }) })
      : t('invoiceCentral.closedMarket');

  const tooltipContent =
    count !== undefined && invoiceAmount !== undefined
      ? t('core.closedMarketCountTooltip', {
          count,
          formattedCount: asNumber({ value: count }),
          amount: invoiceAmount,
        })
      : t('core.closedMarketDivisionTooltip');

  return (
    <Popover>
      <Popover.Trigger aria-label={tooltipContent}>
        <Chip
          label={label}
          type="warning"
          variant="outlined"
          size="xs"
          useHoverStyles
          startIcon={{ icon: ExclamationTriangleIcon, name: 'warning' }}
        />
      </Popover.Trigger>
      <Popover.Content arrow collisionPadding={24}>
        <div className="w-60 text-center text-sm">{tooltipContent}</div>
      </Popover.Content>
    </Popover>
  );
}
